import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, getErrMsg } from "../lib/api";
import { formatTL, formatDate } from "../lib/format";
import { bugun } from "../lib/sabitler";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Briefcase, Plus, Trash2 } from "lucide-react";

const AYLAR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const FORM_BOS = { tarih: "", hizmet_adi: "", tedarikci: "", matrah: "", kdv: "", faturali: true, fatura_dosya_id: null, dosya_adi: "" };

export const DisKaynakSekmesi = ({ yil = 2026 }) => {
  const [ay, setAy] = useState(String(new Date().getMonth() + 1));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ ...FORM_BOS, tarih: bugun() });
  const [saving, setSaving] = useState(false);
  const [yukluyor, setYukluyor] = useState(false);

  const dosyaYukle = async (file) => {
    if (!file) return;
    setYukluyor(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/dosya-yukle", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, fatura_dosya_id: res.data.dosya_id, dosya_adi: res.data.dosya_adi }));
      toast.success("Fatura yüklendi.");
    } catch (err) {
      toast.error(getErrMsg(err, "Fatura yüklenemedi."));
    } finally {
      setYukluyor(false);
    }
  };

  const fetchListe = useCallback(async () => {
    setLoading(true);
    try {
      const params = { yil };
      if (ay !== "yil") params.ay = parseInt(ay);
      const res = await api.get("/dis-kaynak", { params });
      setData(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Dış kaynak kayıtları yüklenemedi."));
    } finally {
      setLoading(false);
    }
  }, [yil, ay]);

  useEffect(() => {
    fetchListe();
  }, [fetchListe]);

  const handleEkle = async (e) => {
    e.preventDefault();
    if (!form.tarih) return toast.error("Tarih zorunludur.");
    if (!form.hizmet_adi.trim()) return toast.error("Hizmet adı zorunludur.");
    if (!(parseFloat(form.matrah) > 0)) return toast.error("Matrah 0'dan büyük olmalıdır.");
    if (form.faturali && !form.fatura_dosya_id) return toast.error("Faturalı gider için fatura dosyasının yüklenmesi zorunludur.");
    setSaving(true);
    try {
      await api.post("/dis-kaynak", {
        tarih: form.tarih,
        hizmet_adi: form.hizmet_adi.trim(),
        tedarikci: form.tedarikci.trim(),
        matrah: parseFloat(form.matrah),
        kdv: parseFloat(form.kdv) || 0,
        faturali: !!form.faturali,
        fatura_dosya_id: form.fatura_dosya_id || null,
      });
      toast.success("Bağımsız dış kaynak gideri eklendi.");
      setForm({ ...FORM_BOS, tarih: bugun() });
      fetchListe();
    } catch (err) {
      toast.error(getErrMsg(err, "Dış kaynak gideri eklenemedi."));
    } finally {
      setSaving(false);
    }
  };

  const handleSil = async (id) => {
    try {
      await api.delete(`/dis-kaynak/${id}`);
      toast.success("Dış kaynak kaydı silindi.");
      fetchListe();
    } catch (err) {
      toast.error(getErrMsg(err, "Kayıt silinemedi."));
    }
  };

  const kayitlar = data?.kayitlar || [];

  return (
    <div className="space-y-4" data-testid="dis-kaynak-sekmesi">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-amber-600" />
            Dış Kaynak Hizmetleri (Outsource)
          </h2>
          <p className="text-xs text-muted-foreground">
            Bütçe kalemlerine ve Planlanan/Gerçekleşen yapısına karışmaz — ayrı izlenir, Toplam Gider'e ayrı satır olarak eklenir. Faturalı satırlar KDV hariç, faturasız satırlar KDV dahil tutardır.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-3">
          {data && (
            <span className="text-sm font-semibold tabular-nums" data-testid="dis-kaynak-toplam">
              {data.adet} işlem • {formatTL(data.toplam)}
            </span>
          )}
          <Select value={ay} onValueChange={setAy}>
            <SelectTrigger className="h-11 w-44" data-testid="dis-kaynak-ay-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card">
              {AYLAR.map((a, i) => (
                <SelectItem key={a} value={String(i + 1)}>{a} {yil}</SelectItem>
              ))}
              <SelectItem value="yil">Tüm Yıl ({yil})</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Bağımsız (faturasız) dış kaynak girişi */}
      <form onSubmit={handleEkle} className="rounded-xl border bg-card p-4" data-testid="dis-kaynak-form">
        <p className="text-sm font-medium">Bağımsız Dış Kaynak Gideri Ekle</p>
        <p className="text-xs text-muted-foreground">Bir müşteri faturasına bağlı olmayan dış kaynak işleri için. Faturaya bağlı olanları fatura girişindeki "Dış Kaynak Hizmetleri" bölümünden ekleyin.</p>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-[11px]">Tarih *</Label>
            <Input type="date" value={form.tarih} onChange={(e) => setForm((f) => ({ ...f, tarih: e.target.value }))} className="h-11 w-40" data-testid="dis-kaynak-form-tarih" />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px]">Hizmet Adı *</Label>
            <Input value={form.hizmet_adi} onChange={(e) => setForm((f) => ({ ...f, hizmet_adi: e.target.value }))} placeholder="örn. Turbo Revizyonu" className="h-11 w-52" data-testid="dis-kaynak-form-hizmet" />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px]">Tedarikçi</Label>
            <Input value={form.tedarikci} onChange={(e) => setForm((f) => ({ ...f, tedarikci: e.target.value }))} placeholder="Opsiyonel" className="h-11 w-44" data-testid="dis-kaynak-form-tedarikci" />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px]">Matrah (KDV hariç) *</Label>
            <Input type="number" step="0.01" min="0" value={form.matrah} onChange={(e) => setForm((f) => ({ ...f, matrah: e.target.value }))} className="h-11 w-32 tabular-nums" data-testid="dis-kaynak-form-matrah" />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px]">KDV</Label>
            <Input type="number" step="0.01" min="0" value={form.kdv} onChange={(e) => setForm((f) => ({ ...f, kdv: e.target.value }))} className="h-11 w-28 tabular-nums" data-testid="dis-kaynak-form-kdv" />
          </div>
          <div className="space-y-1 pb-2.5">
            <span className="text-xs font-semibold tabular-nums whitespace-nowrap" data-testid="dis-kaynak-form-toplam">
              Toplam: {((parseFloat(form.matrah) || 0) + (parseFloat(form.kdv) || 0)).toLocaleString("tr-TR")} ₺
            </span>
          </div>
          <div className="flex items-center gap-2 pb-2.5">
            <Switch checked={form.faturali} onCheckedChange={(v) => setForm((f) => ({ ...f, faturali: v }))} data-testid="dis-kaynak-form-faturali" />
            <span className="text-xs text-muted-foreground">{form.faturali ? "Faturalı" : "Faturasız"}</span>
          </div>
          {form.faturali && (
            <div className="flex items-center gap-2 pb-1.5">
              <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border bg-card px-3 py-2 text-xs font-medium hover:bg-muted/60">
                {yukluyor ? "Yükleniyor…" : form.fatura_dosya_id ? "Faturayı Değiştir" : "Fatura Yükle *"}
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" className="hidden" onChange={(e) => dosyaYukle(e.target.files?.[0])} data-testid="dis-kaynak-form-dosya" />
              </label>
              {form.fatura_dosya_id && <span className="max-w-[140px] truncate text-xs text-muted-foreground">{form.dosya_adi}</span>}
            </div>
          )}
          <Button type="submit" disabled={saving} className="min-h-[44px] gap-1.5" data-testid="dis-kaynak-form-kaydet">
            <Plus className="h-4 w-4" />
            {saving ? "Kaydediliyor…" : "Ekle"}
          </Button>
        </div>
      </form>

      {/* Liste */}
      {loading && !data ? (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : kayitlar.length === 0 ? (
        <div className="rounded-xl border bg-card p-10 text-center text-sm text-muted-foreground" data-testid="dis-kaynak-bos">
          Bu dönemde dış kaynak gideri yok.
        </div>
      ) : (
        <div className="rounded-xl border bg-card overflow-hidden">
          <div className="w-full overflow-x-auto">
            <table className="w-full text-sm" data-testid="dis-kaynak-tablo">
              <thead className="bg-[hsl(var(--primary))] text-white">
                <tr className="text-left text-xs uppercase tracking-wide">
                  <th className="px-3 py-3">Tarih</th>
                  <th className="px-3 py-3 min-w-[160px]">Hizmet</th>
                  <th className="px-3 py-3">Tedarikçi</th>
                  <th className="px-3 py-3">Kaynak</th>
                  <th className="px-3 py-3">Fatura</th>
                  <th className="px-3 py-3 text-right">Matrah</th>
                  <th className="px-3 py-3 text-right">KDV</th>
                  <th className="px-3 py-3 text-right">Toplam</th>
                  <th className="px-3 py-3 w-14"></th>
                </tr>
              </thead>
              <tbody>
                {kayitlar.map((k) => (
                  <tr key={k.id} className="border-t border-border/60" data-testid="dis-kaynak-satir">
                    <td className="px-3 py-2.5 whitespace-nowrap tabular-nums">{formatDate(k.tarih)}</td>
                    <td className="px-3 py-2.5 font-medium">{k.hizmet_adi}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">{k.tedarikci || "—"}</td>
                    <td className="px-3 py-2.5">
                      {k.kaynak === "fatura" ? (
                        <Badge className="border border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-50" title={`Müşteri: ${k.musteri || "—"}${k.plaka ? " • " + k.plaka : ""}`}>
                          Fatura içi
                        </Badge>
                      ) : (
                        <Badge className="border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-50">Bağımsız</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {k.fatura_dosya_id ? (
                        <a
                          href={`${api.defaults.baseURL}/ciro/fatura-pdf/${k.fatura_dosya_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs font-medium text-[hsl(var(--primary))] underline underline-offset-2"
                          data-testid={`dis-kaynak-fatura-link-${k.id}`}
                        >
                          Faturayı Gör
                        </a>
                      ) : (
                        <span className="text-xs text-muted-foreground">{k.faturali ? "Faturalı" : "Faturasız"}</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{formatTL(k.matrah, 2)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap text-muted-foreground">{formatTL(k.kdv, 2)}</td>
                    <td className="px-3 py-2.5 text-right font-semibold tabular-nums whitespace-nowrap">{formatTL(k.toplam, 2)}</td>
                    <td className="px-3 py-2.5 text-right">
                      {k.kaynak === "bagimsiz" && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-600" onClick={() => handleSil(k.id)} data-testid={`dis-kaynak-sil-${k.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
